import React, { useState, useEffect } from "react";
import { Container } from "react-bootstrap";
import { CgCPlusPlus } from "react-icons/cg";
import {
  DiJavascript1,
  DiReact,
  DiNodejs,
  DiMongodb,
  DiPython,
  DiGit,
  DiJava,
  DiHtml5,
  DiCss3,
  DiDocker,
  DiAws,
} from "react-icons/di";
import {
  SiRedis,
  SiFirebase,
  SiNextdotjs,
  SiPostgresql,
  SiSpringboot,
  SiSymfony,
  SiTypescript,
  SiKubernetes,
  SiTailwindcss,
  SiGraphql,
  SiMysql,
  SiGithubactions,
  SiJenkins,
  SiVisualstudiocode,
  SiPostman,
  SiIntellijidea,
  SiSwagger,
  SiGithub,
  SiJira,
  SiNotion,
  SiFigma,
  SiDart,
} from "react-icons/si";
import { TbBrandAngular, TbBrandGolang } from "react-icons/tb";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import "./SkillCarouselStyles.css";

const SkillCarousel = () => {
  const [activeCategory, setActiveCategory] = useState("languages");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [autoplay, setAutoplay] = useState(true);

  // Technology categories with their icons
  const skillsData = {
    languages: {
      title: "Programming Languages",
      icons: [
        { Icon: DiJavascript1, name: "JavaScript", color: "#F7DF1E" },
        { Icon: SiTypescript, name: "TypeScript", color: "#3178C6" },
        { Icon: DiPython, name: "Python", color: "#3776AB" },
        { Icon: DiJava, name: "Java", color: "#007396" },
        { Icon: CgCPlusPlus, name: "C++", color: "#00599C" },
        { Icon: TbBrandGolang, name: "Golang", color: "#00ADD8" },
        { Icon: SiDart, name: "Dart", color: "#0175C2" }
      ]
    },
    frontend: {
      title: "Frontend Technologies",
      icons: [
        { Icon: DiReact, name: "React", color: "#61DAFB" },
        { Icon: TbBrandAngular, name: "Angular", color: "#DD0031" },
        { Icon: SiNextdotjs, name: "Next.js", color: "#FFFFFF" },
        { Icon: DiHtml5, name: "HTML5", color: "#E34F26" },
        { Icon: DiCss3, name: "CSS3", color: "#1572B6" },
        { Icon: SiTailwindcss, name: "Tailwind", color: "#38B2AC" }
      ]
    },
    backend: {
      title: "Backend Technologies",
      icons: [
        { Icon: DiNodejs, name: "Node.js", color: "#339933" },
        { Icon: SiSpringboot, name: "Spring Boot", color: "#6DB33F" },
        { Icon: SiSymfony, name: "Symfony", color: "#000000" },
        { Icon: SiGraphql, name: "GraphQL", color: "#E10098" }
      ]
    },
    database: {
      title: "Databases & Storage",
      icons: [
        { Icon: DiMongodb, name: "MongoDB", color: "#47A248" },
        { Icon: SiPostgresql, name: "PostgreSQL", color: "#336791" },
        { Icon: SiMysql, name: "MySQL", color: "#4479A1" },
        { Icon: SiRedis, name: "Redis", color: "#DC382D" },
        { Icon: SiFirebase, name: "Firebase", color: "#FFCA28" }
      ]
    },
    devops: {
      title: "DevOps & Cloud",
      icons: [
        { Icon: DiGit, name: "Git", color: "#F05032" },
        { Icon: DiDocker, name: "Docker", color: "#2496ED" },
        { Icon: SiKubernetes, name: "Kubernetes", color: "#326CE5" },
        { Icon: DiAws, name: "AWS", color: "#FF9900" },
        { Icon: SiGithubactions, name: "GitHub Actions", color: "#2088FF" },
        { Icon: SiJenkins, name: "Jenkins", color: "#D24939" }
      ]
    },
    tools: {
      title: "Development Tools",
      icons: [
        { Icon: SiVisualstudiocode, name: "VS Code", color: "#007ACC" },
        { Icon: SiIntellijidea, name: "IntelliJ IDEA", color: "#000000" },
        { Icon: SiPostman, name: "Postman", color: "#FF6C37" },
        { Icon: SiSwagger, name: "Swagger", color: "#85EA2D" },
        { Icon: SiGithub, name: "GitHub", color: "#FFFFFF" },
        { Icon: SiJira, name: "Jira", color: "#0052CC" },
        { Icon: SiNotion, name: "Notion", color: "#FFFFFF" },
        { Icon: SiFigma, name: "Figma", color: "#F24E1E" }
      ]
    }
  };

  // Categories for the tabs
  const categories = [
    { id: 'languages', name: 'Languages' },
    { id: 'frontend', name: 'Frontend' },
    { id: 'backend', name: 'Backend' },
    { id: 'database', name: 'Databases' },
    { id: 'devops', name: 'DevOps' },
    { id: 'tools', name: 'Tools' }
  ];

  // Handle category change
  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setActiveIndex(0);
  };

  // Handle navigation
  const navigate = (direction) => {
    if (isAnimating) return;

    setIsAnimating(true);
    const currentIcons = skillsData[activeCategory].icons;
    const newIndex = direction === 'next' 
      ? (activeIndex + 1) % currentIcons.length
      : (activeIndex - 1 + currentIcons.length) % currentIcons.length;
    
    setActiveIndex(newIndex);
    
    // Reset animation flag
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  // Autoplay effect
  useEffect(() => {
    let interval;
    
    if (autoplay) {
      interval = setInterval(() => {
        if (!isAnimating) {
          navigate('next');
        }
      }, 3000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay, isAnimating, activeIndex, activeCategory]);

  // Get visible icons
  const getVisibleIcons = () => {
    const currentIcons = skillsData[activeCategory].icons;
    const totalIcons = currentIcons.length;
    const visibleCount = Math.min(5, totalIcons); // Show up to 5 icons at once
    
    const icons = [];
    for (let i = 0; i < visibleCount; i++) {
      const index = (activeIndex + i) % totalIcons;
      icons.push(currentIcons[index]);
    }
    
    return icons;
  };

  return (
    <Container fluid className="skill-carousel-container">
      <div className="skills-tab-container">
        <div className="skills-tabs">
          {categories.map(category => (
            <button
              key={category.id}
              className={`skill-tab ${activeCategory === category.id ? 'active' : ''}`}
              onClick={() => handleCategoryChange(category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>
      
      <div className="skills-display-container">
        <h3 className="skills-category-title">{skillsData[activeCategory].title}</h3>
        
        <div className="skills-carousel">
          <button 
            className="carousel-nav prev"
            onClick={() => {
              navigate('prev');
              setAutoplay(false);
            }}
            aria-label="Previous skill"
          >
            <IoIosArrowBack />
          </button>
          
          <div className="skills-display">
            {getVisibleIcons().map((tech, index) => (
              <div 
                key={tech.name}
                className="skill-item"
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
              >
                <div className="skill-icon">
                  <tech.Icon style={{ color: tech.color }} />
                </div>
                <span className="skill-name">{tech.name}</span>
              </div>
            ))}
          </div>
          
          <button 
            className="carousel-nav next"
            onClick={() => {
              navigate('next');
              setAutoplay(false);
            }}
            aria-label="Next skill"
          >
            <IoIosArrowForward />
          </button>
        </div>
        
        <div className="skill-indicators">
          {skillsData[activeCategory].icons.map((_, index) => (
            <div 
              key={index}
              className={`indicator ${index === activeIndex ? 'active' : ''}`}
              onClick={() => {
                setActiveIndex(index);
                setAutoplay(false);
              }}
            />
          ))}
        </div>
      </div>
      
      <div 
        className="autoplay-toggle" 
        onClick={() => setAutoplay(!autoplay)}
      >
        {autoplay ? 'Pause Autoplay' : 'Resume Autoplay'}
      </div>
    </Container>
  );
};

export default SkillCarousel;